import { createApi, fetchBaseQuery } from '@reduxjs/toolkit/query/react';
import { differenceInYears, parseISO } from 'date-fns';
import { basicHeaders, legacyFulfill } from '../utils';
import {
  LICENSE_RECEIVED_CONSUME_LICENSE,
  LICENSE_RECEIVED_GET_LICENSE_REPORT,
  LICENSE_RECEIVED_GET_LICENSE_REPORT_LIST,
  LICENSE_RECEIVED_UPDATE_LICENSES,
} from '../../legacy/actions/license-actions';

export interface LocationReport {
  id: number;
  asset_serial: string;
  asset_type: string;
  license_serial: string;
  created_at: string;
  updated_at: string;
  po_number: string;
  location_id: number;
  jde_number: string;
  sf_number: string;
  consumed: boolean;
  consumed_at: string;
  duration?: number | string;
  start_date?: string;
  end_date?: string;
}

export interface UpdateLicensePayload {
  license_serial: string;
  location_id: number;
  duration: number;
}

export const getLicenseDurationInYears = (
  license: Pick<LocationReport, 'duration' | 'start_date' | 'end_date'>
): number | undefined => {
  if (license.duration !== undefined && license.duration !== null && license.duration !== '') {
    const duration = Number(license.duration);
    if (Number.isFinite(duration)) return duration;
  }
  if (!license.start_date || !license.end_date) return undefined;

  const startDate = parseISO(license.start_date);
  const endDate = parseISO(license.end_date);
  if (Number.isNaN(startDate.getTime()) || Number.isNaN(endDate.getTime())) return undefined;

  const years = differenceInYears(endDate, startDate);
  return years >= 0 ? years : undefined;
};

export interface LicenseStatus {
  asset_serial: string;
  is_licensed: boolean;
  start_date?: string;
  location_id?: number;
  license_serial?: string;
}

export interface ConsumeLicensePayload {
  asset_serial: string;
  license_serial: string;
}

export const locationReportApi = createApi({
  reducerPath: 'locationReportApi',
  baseQuery: fetchBaseQuery({
    baseUrl: `${import.meta.env.VITE_APP_LICENSE_SERVICE}`,
    prepareHeaders: basicHeaders,
  }),
  tagTypes: ['locationReport'],
  endpoints: (builder) => ({
    consumeLicense: builder.query<LocationReport, LocationReport[]>({
      query: (licenses) => ({
        url: 'service/consume-license',
        method: 'PUT',
        body: licenses,
      }),
      onQueryStarted: legacyFulfill(LICENSE_RECEIVED_CONSUME_LICENSE),
    }),
    getLocationReport: builder.query<LocationReport[], number>({
      query: (locationId) => `/location-report/${locationId}`,
      onQueryStarted: legacyFulfill(LICENSE_RECEIVED_GET_LICENSE_REPORT),
      providesTags: ['locationReport'],
    }),
    getLocationReportList: builder.mutation<LocationReport[], { list: number[] }>({
      query: (list) => ({
        url: '/location-report',
        method: 'PUT',
        body: { location_ids: list },
      }),
      onQueryStarted: legacyFulfill(LICENSE_RECEIVED_GET_LICENSE_REPORT_LIST),
    }),
    updateLicenses: builder.mutation<
      LocationReport[],
      UpdateLicensePayload[]
    >({
      query: (licenses) => ({
        url: `/update-license`,
        method: 'PUT',
        body: { licenses },
      }),
      onQueryStarted: legacyFulfill(LICENSE_RECEIVED_UPDATE_LICENSES),
      invalidatesTags: ['locationReport'],
    }),
    consumeLicenses: builder.mutation<LocationReport[], ConsumeLicensePayload[]>({
      query: (licenses) => ({
        url: '/consume-license',
        method: 'PUT',
        body: { licenses },
      }),
      invalidatesTags: ['locationReport'],
    }),
    getLicenseStatus: builder.query<LicenseStatus[], string[]>({
      query: (list) => ({
        url: '/license-status',
        method: 'PUT',
        body: {
          assets: list.map((a) => {
            return { asset_serial: a };
          }),
        },
      }),

      providesTags: ['locationReport'],
    }),
  }),
});

export const {
  useConsumeLicenseQuery,
  useGetLocationReportQuery,
  useUpdateLicensesMutation,
  useGetLocationReportListMutation,
  useLazyGetLocationReportQuery,
  useLazyConsumeLicenseQuery,
  useConsumeLicensesMutation,
  useGetLicenseStatusQuery,
} = locationReportApi;
